import { Box, IconButton, Theme, Typography, useTheme, Fade } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import bannerImage from './../../../../assets/connect/match-quiz-banner-asset.png';
import { SecondaryButton } from 'sharedComponents/Buttons';
import { handleOpenConnectMatchQuiz } from 'features/Connect/utils/connectUtils';
import { ConnectMatchQuizAccessBannerDataTestIds } from 'data-testids/ConnectDataTestIds';
import auth from 'utils/auth';
import { useState } from 'react';

export const ConnectMatchQuizBanner: React.FC<{ onDismiss?: () => void }> = ({ onDismiss }) => {
  const theme = useTheme();
  const user = auth.getUser();
  const [open, setOpen] = useState(true);

  const handleTakeQuiz = () => {
    handleOpenConnectMatchQuiz(user.id);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Fade in={open} onExited={onDismiss} timeout={300} unmountOnExit>
      <BannerContainer data-testid={ConnectMatchQuizAccessBannerDataTestIds.BANNER}>
        <ImageSection>
          <BannerImage
            src={bannerImage}
            alt={'Banner Image'}
            data-testid={ConnectMatchQuizAccessBannerDataTestIds.IMAGE}
          />
        </ImageSection>
        <ContentSection>
          <Title data-testid={ConnectMatchQuizAccessBannerDataTestIds.TITLE}>
            Get better matches
          </Title>
          <Subtitle data-testid={ConnectMatchQuizAccessBannerDataTestIds.SUBTITLE}>
            Take our preference quiz and supercharge your job search
          </Subtitle>
        </ContentSection>
        <ActionsSection>
          <SecondaryButton
            onClick={handleTakeQuiz}
            sx={MatchButtonStyles({ theme })}
            dataTestId={ConnectMatchQuizAccessBannerDataTestIds.TAKE_QUIZ_BUTTON}
          >
            Take Your Match Quiz
          </SecondaryButton>
          <CloseButtonWrapper>
            <IconButton
              onClick={handleClose}
              data-testid={ConnectMatchQuizAccessBannerDataTestIds.CLOSE_BUTTON}
            >
              <CloseIcon sx={{ color: theme.palette.white.main }} />
            </IconButton>
          </CloseButtonWrapper>
        </ActionsSection>
      </BannerContainer>
    </Fade>
  );
};

// Styled components
const BannerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'center',
  width: '100%',
  minHeight: '64px',
  background: `linear-gradient(90deg, ${theme.palette.blue.mediumDark} 0%, ${theme.palette.purple.dark} 100%)`,
  padding: theme.spacing(1.5, 32),
  boxShadow: theme.shadows[2],
  position: 'relative',
  [theme.breakpoints.down('lg')]: {
    padding: theme.spacing(3, 1, 2),
    flexDirection: 'column',
  },
}));

const ImageSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const BannerImage = styled('img')(() => ({
  width: 100,
  height: 65,
  flexShrink: 0,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flex: 1,
  minWidth: 0,
  [theme.breakpoints.down('lg')]: {
    alignContent: 'center',
    justifyContent: 'center',
    justifyItems: 'center',
    textAlign: 'center',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  fontSize: theme.typography.h2.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '30px',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.white.main,
  fontSize: theme.typography.heading7.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: '26px',
}));

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: theme.spacing(2),
}));

const MatchButtonStyles = ({ theme }: { theme: Theme }) => ({
  display: 'inline-flex',
  padding: '8px 24px',
  color: theme.palette.white.main,
  fontSize: theme.typography.heading7.fontSize,
  fontStyle: 'normal',
  fontWeight: theme.typography.fontWeightMediumBold,
  lineHeight: '26px',
  textTransform: 'capitalize',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  borderRadius: theme.shape.borderRadius * 0.75,
  border: `2px solid ${theme.palette.white.main}`,
  backgroundColor: 'transparent',
});

const CloseButtonWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    position: 'static',
    marginLeft: theme.spacing(1),
  },
}));
